import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentProductId } from './CurrentProductIdContext';
import isURLValid from './helpers/isURLValid';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
import { useTranslation } from './i18n';

export type CustomHelpContextValue = {
  link: string | undefined;
  setLink: (link: string) => void;
};

const CONTEXT_NAME: string =
  (CONTEXT_NAMES as Record<string, string>).CustomHelpContext ||
  'CustomHelpContext';

export const CustomHelpContext =
  getOrCreateGlobalContext<CustomHelpContextValue>(CONTEXT_NAME);

const useCustomHelpLinkValue = () =>
  useContextWithValidation(CustomHelpContext, {
    hook: 'useCustomHelpLinkValue',
    provider: 'CustomHelpProvider',
  });

export const useCustomHelpLink = () => {
  const { link: customHelpLink, setLink: setCustomHelpLink } =
    useCustomHelpLinkValue();
  const originalLinkRef = useRef<string | undefined | null>(null);

  useEffect(() => {
    if (originalLinkRef.current === null) {
      originalLinkRef.current = customHelpLink;
    }

    return () => {
      // restore original value when the component is unmounted
      if (
        originalLinkRef.current !== null &&
        originalLinkRef.current !== undefined
      ) {
        setCustomHelpLink(originalLinkRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCustomHelpLinkWithRestore = useCallback(
    (link: string) => {
      if (!link) {
        throw new Error('useCustomHelpLink: Link cannot be empty or undefined');
      }

      if (!isURLValid(link)) {
        if (!link.startsWith('http://') && !link.startsWith('https://')) {
          throw new Error(
            `useCustomHelpLink: URL must include http:// or https:// protocol: ${link}`,
          );
        }

        throw new Error(`useCustomHelpLink: Invalid URL provided: ${link}`);
      }

      if (link !== customHelpLink) {
        setCustomHelpLink(link);
      }
    },
    [customHelpLink, setCustomHelpLink],
  );
  return { link: customHelpLink, setLink: setCustomHelpLinkWithRestore };
};

type CustomHelpProviderProps = {
  children?: ReactNode;
};

/**
 * Provider to expose custom help link for remote applications.
 * Ensures only a single provider instance exists in the React tree.
 */
export const CustomHelpProvider: FC<CustomHelpProviderProps> = ({
  children,
}) => {
  useProviderOnlyOnce({
    context: CustomHelpContext,
    contextName: CONTEXT_NAME,
  });

  const currentProductId = useCurrentProductId();
  const { t } = useTranslation();

  const [link, setLink] = useState<string | undefined>();

  useEffect(() => {
    if (currentProductId) {
      const productHelpLink = t(`${currentProductId.toUpperCase()}_HELP_LINK`);
      if (productHelpLink && isURLValid(productHelpLink)) {
        setLink(productHelpLink);
      }
    }
  }, [t, currentProductId]);

  const setCustomHelpLink = useCallback((link: string) => {
    setLink(link);
  }, []);

  const value = useMemo<CustomHelpContextValue>(
    () => ({ link, setLink: setCustomHelpLink }),
    [link, setCustomHelpLink],
  );

  return (
    <CustomHelpContext.Provider value={value}>
      {children}
    </CustomHelpContext.Provider>
  );
};
